import { QueryHookOptions, useApolloClient, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { RecordFolder } from 'src/graphql';
import { UPDATE_RECORD_FOLDERS_QUERY } from 'src/graphql/entities/record-folder/record-folder.operations';

export interface UpdateRecordFolder {
  updateRecordFolder: RecordFolder[];
}

export const useUpdateRecordFolder = () => {
  const client = useApolloClient();
  const [updateRecordFolder, { loading: isLoading }] = useMutation<UpdateRecordFolder>(
    UPDATE_RECORD_FOLDERS_QUERY,
  );

  const updateWithOptimisticResponse = useCallback(
    (
      original: RecordFolder,
      patch: Partial<RecordFolder>,
      onCompleted?: QueryHookOptions['onCompleted'],
    ) => {
      const omitIsolationModeHeader = original.partnerId === PLATFORM_PARTNER_ID;
      const response = updateRecordFolder({
        variables: {
          data: patch,
          recordFolderId: original.id,
        },
        optimisticResponse: {
          updateRecordFolder: [
            {
              ...original,
              ...patch,
            },
          ],
        },
        onCompleted,
        context: omitIsolationModeHeader ? { omitIsolationModeHeader: true } : undefined,
      });

      if (omitIsolationModeHeader) {
        void response
          .then(() => {
            client.cache.evict({
              fieldName: 'getEnabledRecordFolders',
            });
            client.cache.gc();
          })
          .catch(() => undefined);
      }
    },
    [client, updateRecordFolder],
  );

  return {
    updateRecordFolder: updateWithOptimisticResponse,
    isLoading,
  };
};
